import { useState } from 'react';

import { useCountdown } from '@/hooks/use-countdown';

import { AUTH_POLICY } from './constants';
import type { OtpDispatch } from './types';

type Init = {
  resendCooldownSeconds: number;
  resendAttemptsRemaining: number;
  otpExpiresAt: string;
};

export function useOtpChallenge(init: Init) {
  const [resendDeadline, setResendDeadline] = useState(
    () => Date.now() + init.resendCooldownSeconds * 1000,
  );
  const [attemptsRemaining, setAttemptsRemaining] = useState(init.resendAttemptsRemaining);
  const [expiresAt, setExpiresAt] = useState(() => new Date(init.otpExpiresAt).getTime());

  const resendSecondsLeft = useCountdown(resendDeadline);
  const expirySecondsLeft = useCountdown(expiresAt);

  const canResend = resendSecondsLeft === 0 && attemptsRemaining > 0;
  const expired = expirySecondsLeft === 0;

  function restartCooldown(seconds: number) {
    setResendDeadline(Date.now() + seconds * 1000);
  }

  /** A resend restarts the cooldown **and** the code's validity window (US-117548 5h). */
  function onResend(dispatch: OtpDispatch) {
    restartCooldown(dispatch.resendCooldownSeconds);
    setAttemptsRemaining(dispatch.resendAttemptsRemaining ?? AUTH_POLICY.maxResends);
    setExpiresAt(new Date(dispatch.otpExpiresAt).getTime());
  }

  /** The server's 429 rate limit: the allowance is spent, whatever the client counted. */
  function exhaustAttempts() {
    setAttemptsRemaining(0);
  }

  return {
    resendSecondsLeft,
    attemptsRemaining,
    canResend,
    expired,
    onResend,
    restartCooldown,
    exhaustAttempts,
  };
}
